import type { ChunkWithDistances, SeedWithDistances } from "@storytell/client";
import { dispatchGlobalEvent } from "@storytell/events";
import { SolidStreamingMarkdown, createSolidStreamingMarkdown } from "@storytell/markdown";
import { StButton } from "@storytell/ui/buttons";
import dayjs from "dayjs";
import { TbMessage2 } from "solid-icons/tb";
import { createMemo, Suspense, Show, For, createSignal } from "solid-js";
import { useWire } from "~/wire";
import { useAppUI } from "../layouts/minimal/AppUIContext";
import { MockSubscribeToConcept } from "./MockSubscribeToConcept";
import { Dynamic } from "solid-js/web";
import { getAssetIcon } from "../utils/getAssetIcon";
import type { AssetId, ConceptId } from "@storytell/types";

export const NavigationConceptsPaneDetails = (props: {
  id: ConceptId | "";
  onConceptClick: (id: ConceptId | "") => void;
}) => {
  const wire = useWire();
  const concept = () => (props.id ? wire.sync.concepts.get(props.id) : undefined);

  const assetsTimelineFromSeeds = createMemo(() => {
    const timeline: { seed: SeedWithDistances; chunks: ChunkWithDistances[]; assetId: AssetId }[] = [];

    for (const seed of concept()?.seeds ?? []) {
      const chunksByAssetId: Record<AssetId, ChunkWithDistances[]> = {};
      for (const chunk of seed.chunks ?? []) {
        if (!chunksByAssetId[chunk.assetId]) {
          chunksByAssetId[chunk.assetId] = [];
        }
        chunksByAssetId[chunk.assetId].push(chunk);
      }

      for (const assetId in chunksByAssetId) {
        timeline.push({
          seed,
          chunks: chunksByAssetId[assetId as AssetId].sort((a, b) => dayjs(a.modifiedAt).diff(dayjs(b.modifiedAt))),
          assetId: assetId as AssetId,
        });
      }
    }

    return timeline;
  });

  const relatedAssets = () => Array.from(new Set(assetsTimelineFromSeeds().map((a) => a.assetId)));

  const relatedConcepts = () =>
    Object.entries(concept()?.distances ?? {})
      .filter((c) => c[1] <= 0.75)
      .map((c) => wire.sync.concepts.get(c[0] as ConceptId));

  const onAssetClick = (assetId: string) => {
    dispatchGlobalEvent("open-asset-in-pane", { assetId });
  };
  let ref!: HTMLDivElement;

  const { actions } = useAppUI();
  return (
    <div ref={ref}>
      <Suspense>
        <Show when={concept()}>
          {(concept) => (
            <div class="space-y-8 px-5 pb-10">
              <div class="mt-4 flex items-center justify-between gap-3">
                <div>
                  <h2 class="text-xl font-semibold mb-2">{concept().name}</h2>
                  <p class="text-base text-secondary-text">{concept().summary}</p>
                </div>
                <div class="space-y-3 shrink-0 flex flex-col justify-end">
                  <StButton
                    noColor
                    class="bg-brand hover:bg-brand-hover text-on-brand shrink-0 text-sm"
                    onClick={() => {
                      dispatchGlobalEvent("add-items-to-prompt-scope", { ids: [concept().id] });
                    }}
                    icon={TbMessage2}
                  >
                    Chat with this Concept
                  </StButton>

                  <MockSubscribeToConcept mount={ref} />
                </div>
              </div>

              <div class="space-y-4">
                <h4 class="font-medium">Timeline</h4>
                <For each={assetsTimelineFromSeeds()}>
                  {(a, index) => {
                    const [showAllChunks, setShowAllChunks] = createSignal(false);

                    return (
                      <Show when={wire.sync.assets.get(a.assetId)}>
                        {(asset) => (
                          <div class="flex items-start gap-2 relative">
                            <Show when={index() !== assetsTimelineFromSeeds().length - 1}>
                              <div class="absolute top-8 left-[0.9125rem] -bottom-4 w-0.5 bg-primary-border" />
                            </Show>
                            <div class="w-8 h-8 shrink-0 bg-primary-border rounded-lg flex items-center justify-center">
                              <Dynamic component={getAssetIcon(asset())} class="w-4 h-4 text-primary-text" />
                            </div>
                            <div class="">
                              <p class="font-medium text-lg">
                                {a.seed.name}
                                <span class="text-secondary-text text-xs font-normal">
                                  {" "}
                                  - {a.chunks[0]?.modifiedAt ? dayjs(a.chunks[0]?.modifiedAt).format("LLL") : ""}
                                </span>
                              </p>
                              <p class="text-sm mb-2">
                                from{" "}
                                <button
                                  type="button"
                                  class="font-medium text-brand cursor-pointer hover:underline"
                                  onClick={() => onAssetClick(a.assetId)}
                                >
                                  {asset().fileName}
                                </button>
                              </p>
                              <div class="space-y-2 mb-3">
                                <For each={showAllChunks() ? a.chunks : a.chunks.slice(0, 1)}>
                                  {(c) => {
                                    const [clamped, setClamped] = createSignal(true);
                                    return (
                                      // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                                      <div
                                        onClick={() => setClamped(!clamped())}
                                        class="cursor-pointer w-full p-1 px-2 hover:bg-tile-hover transition-colors rounded-xl border border-muted-border dark:border-primary-border"
                                        classList={{ "[&>.md-document]:line-clamp-2": clamped() }}
                                      >
                                        <SolidStreamingMarkdown
                                          doc={
                                            createSolidStreamingMarkdown(
                                              c.text.replaceAll(/\[(source:.*|\d+)\]/g, ""), //.replaceAll("\n", "\n\n")
                                            ).doc
                                          }
                                          options={{
                                            classes: {
                                              document:
                                                "wrap-break-word whitespace-break-spaces text-sm  [&_*:last-child]:mb-0",
                                              heading: (node) => {
                                                switch (node.level) {
                                                  case 1:
                                                    return "font-medium text-base mb-1";
                                                  case 2:
                                                    return "font-medium text-base mb-1";
                                                  case 3:
                                                    return "font-medium text-base mb-1";
                                                  case 4:
                                                    return "font-medium text-base mb-1";
                                                  case 5:
                                                    return "font-medium text-sm mb-1";
                                                  case 6:
                                                    return "font-medium text-sm mb-1";
                                                  default:
                                                    return "";
                                                }
                                              },
                                              // paragraph: "mb-2",
                                              list: (node) => {
                                                switch (node.listData?.type) {
                                                  case "ordered":
                                                    return "list-decimal pl-4";
                                                  case "bullet":
                                                    return "list-disc pl-4";
                                                  default:
                                                    return "";
                                                }
                                              },
                                              paragraph: "mb-4",
                                              // text: "animate-fade-in",
                                              item: "mb-2",
                                              code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
                                              blockQuote:
                                                "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
                                              tableWrapper:
                                                "overflow-x-auto w-full max-w-full rounded-lg border border-muted-border",
                                              table: "",
                                              tableRow: "border-b border-muted-border [&:last-child]:border-b-0",
                                              tableCell:
                                                "px-2 py-2 border-r border-muted-border [&:last-child]:border-r-0",
                                              tableHead: "bg-panel text-sm font-semibold [&_td]:whitespace-nowrap",
                                              tableBody: "bg-tile",
                                              link: "text-brand underline",
                                            },
                                          }}
                                        />
                                      </div>
                                    );
                                  }}
                                </For>
                              </div>
                              <Show when={a.chunks.length > 2}>
                                <button
                                  onClick={() => setShowAllChunks(!showAllChunks())}
                                  type="button"
                                  class="text-xs text-brand cursor-pointer hover:underline"
                                >
                                  {showAllChunks()
                                    ? " Show less"
                                    : ` + ${a.chunks.length - 2} more source${a.chunks.length - 2 === 1 ? "" : "s"}`}
                                </button>
                              </Show>
                            </div>
                          </div>
                        )}
                      </Show>
                    );
                  }}
                </For>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col justify-start gap-2">
                  <h4 class="font-medium">Related Assets</h4>
                  <For each={relatedAssets()}>
                    {(a) => (
                      <div>
                        <button
                          type="button"
                          class="text-sm text-left text-brand font-semibold hover:underline cursor-pointer truncate whitespace-normal"
                          onClick={() => onAssetClick(a)}
                        >
                          {wire.sync.assets.get(a)?.fileName}
                        </button>
                      </div>
                    )}
                  </For>
                </div>
                <div class="flex flex-col justify-start gap-2">
                  <h4 class="font-medium">Related Concepts</h4>
                  <For each={relatedConcepts()}>
                    {(c) => (
                      <div>
                        <button
                          type="button"
                          class="text-sm text-left text-brand font-semibold hover:underline cursor-pointer truncate whitespace-normal"
                          onClick={() => props.onConceptClick(c?.id ?? "")}
                        >
                          {c?.name}
                        </button>
                      </div>
                    )}
                  </For>
                </div>
              </div>
              {/*
              <div class="space-y-4">
                <For each={concept().seeds}>
                  {(seed) => (
                    <div class="space-y-2 rounded border border-primary-border p-4">
                      <h3>{seed.name}</h3>
                      <p>{seed.summary}</p>

                      <For each={seed.chunks}>
                        {(chunk) => (
                          <div class="mb-1 rounded border border-primary-border p-2">
                            <p>{chunk.assetId}</p>
                            <p>{dayjs(chunk.modifiedAt).format("LLL")}</p>
                            <p class="line-clamp-2">{chunk.text}</p>
                          </div>
                        )}
                      </For>
                    </div>
                  )}
                </For>
              </div> */}
            </div>
          )}
        </Show>
      </Suspense>
    </div>
  );
};
